<script>
import { useAlert } from "dashboard/composables";

export default {
  name: "WahaCtwaCard",
  props: { inbox: { type: Object, required: true } },
  data() {
    return {
      loading: false,
      saving: false,
      config: { configured: false, applicable: false, master_key_set: false }
    };
  },
  computed: {
    accountId() { return window.location.pathname.match(/accounts\/(\d+)/)?.[1] || 1; },
    baseEndpoint() { return "/api/v1/accounts/" + this.accountId + "/inboxes/" + this.inbox.id + "/waha/ctwa"; },
    isActive() { return this.config.active === true; },
    eventsCount() { return this.config.events_received_count || 0; },
    canActivate() { return this.config.applicable && this.config.master_key_set; },
    statusLabel() {
      if (!this.config.master_key_set) return "WAHA_API_KEY nao configurada";
      if (!this.config.meta_capi_active) return "Meta CAPI nao ativo no funnel";
      if (!this.config.applicable) return "Inbox incompativel com WAHA";
      if (this.isActive) return "Ativa";
      if (this.config.configured) return "Configurada (desativada)";
      return "Disponivel";
    },
    statusColor() {
      if (this.isActive) return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300";
      if (!this.canActivate) return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
      return "bg-n-alpha-2 text-n-slate-11";
    }
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get(this.baseEndpoint);
        this.config = data;
      } catch (e) { useAlert("Erro ao carregar configuracao CTWA"); }
      finally { this.loading = false; }
    },
    async toggleActive() {
      this.saving = true;
      try {
        const url = this.baseEndpoint + (this.isActive ? "/disable" : "/enable");
        const { data } = await axios.post(url);
        this.config = { ...this.config, ...data };
        useAlert(this.isActive ? "Interceptacao ativada (session reinicia em ~10s)" : "Interceptacao desativada");
      } catch (e) {
        useAlert(e.response?.data?.error || "Erro ao alternar estado");
      } finally { this.saving = false; }
    },
    formatDate(s) { return s ? new Date(s).toLocaleString("pt-BR") : "-"; }
  }
};
</script>

<template>
  <div class="bg-n-solid-1 border border-n-weak rounded-2xl p-5 mb-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-3">
        <span class="i-ri-megaphone-line size-5 text-n-brand" />
        <div>
          <h3 class="text-sm font-semibold text-n-slate-12">Captura de anuncios CTWA</h3>
          <p class="text-xs text-n-slate-10 mt-0.5">Intercepta o webhook bruto da WAHA pra preservar ctwa_clid e demais infos do Click-to-WhatsApp Ad. Configurada automaticamente.</p>
        </div>
      </div>
      <span :class="['text-xs font-semibold px-2.5 py-1 rounded-full', statusColor]">{{ statusLabel }}</span>
    </div>
    <div v-if="loading" class="text-sm text-n-slate-10">Carregando...</div>
    <div v-else class="space-y-3">
      <div class="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div class="text-n-slate-10 mb-0.5">URL detectada</div>
          <div class="font-mono text-n-slate-12 truncate">{{ config.inferred_base_url || "-" }}</div>
        </div>
        <div>
          <div class="text-n-slate-10 mb-0.5">Session detectada</div>
          <div class="font-mono text-n-slate-12 truncate">{{ config.inferred_session_name || "-" }}</div>
        </div>
      </div>

      <div v-if="!config.master_key_set" class="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2.5">
        <span class="i-ri-alert-line size-3.5 inline-block mr-1" />
        A env var <code class="font-mono">WAHA_API_KEY</code> precisa estar configurada no servidor pra interceptacao funcionar.
      </div>
      <div v-else-if="config.master_key_set && !config.meta_capi_active" class="text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2.5">
        <span class="i-ri-information-line size-3.5 inline-block mr-1" />
        CTWA so e ativada quando o Meta CAPI esta habilitado em algum funnel apontando pra essa inbox. Configure Meta CAPI no funnel primeiro.
      </div>

      <div class="flex items-center gap-3 pt-2">
        <button type="button" :class="['px-4 py-2 text-sm rounded-xl disabled:opacity-50', isActive ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-emerald-600 text-white hover:bg-emerald-700']" :disabled="saving || !canActivate" @click="toggleActive">
          <span v-if="saving" class="i-ri-loader-2-line size-3.5 animate-spin inline-block mr-1" />
          {{ isActive ? "Desativar interceptacao" : "Ativar interceptacao" }}
        </button>

        <div v-if="config.configured" class="text-xs text-n-slate-10">
          <span class="i-ri-pulse-line size-3 inline-block mr-1" />
          {{ eventsCount }} eventos recebidos
          <span v-if="config.last_received_at" class="text-n-slate-9 ml-1">(ultimo: {{ formatDate(config.last_received_at) }})</span>
        </div>
      </div>

      <details v-if="config.configured" class="pt-2 border-t border-n-weak text-xs text-n-slate-10">
        <summary class="cursor-pointer hover:text-n-slate-12">Detalhes tecnicos</summary>
        <div class="mt-2 space-y-1">
          <div><span class="text-n-slate-9">Webhook URL:</span> <span class="font-mono break-all">{{ config.webhook_url }}</span></div>
          <div><span class="text-n-slate-9">Token:</span> <span class="font-mono break-all">{{ config.webhook_token }}</span></div>
        </div>
      </details>
    </div>
  </div>
</template>
